"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Progress } from "@/components/ui/progress"
import {
  Star,
  Eye,
  Pause,
  CheckCircle2,
  Loader2,
  X,
  Edit,
  Send,
  XCircle,
  Play,
  ShoppingBag,
  SealPercent,
  FileDown,
  PenTool,
  Hourglass,
  ShoppingCart,
} from "@/lib/icons"
import { Textarea } from "@/components/ui/textarea"
import { useScriptStore } from "@/lib/script-store"
import { toast } from "sonner"
import { formatPrice, type CommunityScript } from "@/lib/marketplace-store"
import { useCartStore } from "@/lib/cart-store"
import { useAuthStore } from "@/lib/auth-store"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import type { UserScript } from "@/lib/script-store"
import { PreviewWatermarkOverlay } from "@/components/bounty-board/PreviewWatermarkOverlay"
import { EditScriptDialog, getEditorRoute } from "@/components/marketplace/edit-script-dialog"
import { ScriptHeatmap } from "@/components/script-heatmap"
import { ScriptPlayerCore } from "@/components/script-player-modal"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"
import { ProcessorLockedScreen } from "@/components/processor-locked"
import { usePatreonMode } from "@/lib/use-patreon-mode"

interface CommunityScriptDetailModalProps {
  script: CommunityScript | null
  isOpen?: boolean
  open?: boolean
  onOpenChange: (open: boolean) => void
  onPurchaseComplete?: () => void
  onUpdated?: () => void
}

export function CommunityScriptDetailModal({
  script,
  isOpen,
  open,
  onOpenChange,
  onPurchaseComplete,
  onUpdated,
}: CommunityScriptDetailModalProps) {
  const t = useTranslations("communityScriptDetailModal")
  // Patreon mode: paid scripts cannot be bought while there is no payment processor (the server refuses too). Owned scripts still play.
  const { enabled: patreonMode } = usePatreonMode()
  const resolvedOpen = open ?? isOpen ?? false
  const router = useRouter()
  const { user } = useAuthStore()
  const { setScript } = useGlobalPlaybackStore()
  const addScript = useScriptStore((s) => s.addScript)
  const { addItem: addToCart, removeItem: removeFromCart, hasItem: inCart } = useCartStore()
  const [isPurchased, setIsPurchased] = useState(false)
  const [purchaseTier, setPurchaseTier] = useState<"play" | "download" | null>(null)
  const [canDownload, setCanDownload] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [isCreatorVerified, setIsCreatorVerified] = useState(false)
  const [isLoadingPlay, setIsLoadingPlay] = useState(false)
  const [isCheckingOwnership, setIsCheckingOwnership] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  // Rating state
  const [userRating, setUserRating] = useState(0)
  const [ratingHover, setRatingHover] = useState(0)
  const [reviewText, setReviewText] = useState("")
  const [isSubmittingRating, setIsSubmittingRating] = useState(false)
  const [ratingSubmitted, setRatingSubmitted] = useState(false)
  const [existingReviewId, setExistingReviewId] = useState<string | null>(null)

  // Trial state — real playback (Handy sync, heatmap, transport controls) happens via
  // ScriptPlayerCore, embedded inline in place of the thumbnail once a trial starts (not a
  // second stacked dialog); this component just fetches the server-truncated trial content
  // and hands it off. Presence of trialScript alone drives whether the inline player shows.
  const [trialLoading, setTrialLoading] = useState(false)
  const [trialScript, setTrialScript] = useState<UserScript | null>(null)
  const [trialCutoffMs, setTrialCutoffMs] = useState<number | undefined>(undefined)
  const [hasTriedScript, setHasTriedScript] = useState(false)
  const [isTrialPaused, setIsTrialPaused] = useState(false)

  // Coupon state for direct Buy flow
  const [couponInput, setCouponInput] = useState("")
  const [couponData, setCouponData] = useState<{ id: string; code: string; discount_type: string; discount_value: number; discount_cents: number } | null>(null)
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState<string | null>(null)
  // Bumped on every "Try"/"Try again" click and used as ScriptPlayerCore's React key — forces a
  // full remount (fresh engine, fresh autoplay/cutoff guards) each attempt, since the core stays
  // mounted with `open` always true rather than toggling open/closed like a real dialog would.
  const [trialAttempt, setTrialAttempt] = useState(0)

  const isFree = (script?.price_cents ?? 0) === 0
  const trialDuration = script?.trial_duration_seconds ?? 30

  // Check ownership when modal opens — server also tells us isCreator to avoid field mismatch bugs
  useEffect(() => {
    if (!resolvedOpen || !script || !user) {
      setIsPurchased(isFree)
      setPurchaseTier(isFree && !script?.download_price_cents ? "download" : null)
      setCanDownload(isFree && !script?.download_price_cents)
      setCanEdit(isFree && !script?.download_price_cents)
      setIsCreatorVerified(false)
      return
    }

    setIsCheckingOwnership(true)
    fetch(`/api/marketplace/scripts/${script.id}/ownership`)
      .then((r) => r.json())
      .then((data) => {
        setIsPurchased(data.owned === true)
        setIsCreatorVerified(data.isCreator === true)
        setPurchaseTier(data.purchaseTier ?? null)
        setCanDownload(data.canDownload === true)
        setCanEdit(data.canEdit === true)
      })
      .catch(() => {
        setIsPurchased(false)
        setIsCreatorVerified(false)
        setPurchaseTier(null)
        setCanDownload(false)
        setCanEdit(false)
      })
      .finally(() => setIsCheckingOwnership(false))
  }, [resolvedOpen, script?.id, user?.id, isFree, script?.download_price_cents])

  // Reset state when modal closes
  useEffect(() => {
    if (!resolvedOpen) {
      setTrialLoading(false)
      setTrialScript(null)
      setTrialCutoffMs(undefined)
      setHasTriedScript(false)
      setTrialAttempt(0)
      setError(null)
      setUserRating(0)
      setReviewText("")
      setRatingSubmitted(false)
      setExistingReviewId(null)
      setIsCreatorVerified(false)
      setPurchaseTier(null)
      setCanDownload(false)
      setCanEdit(false)
    }
  }, [resolvedOpen])

  // Fetch user's existing review when modal opens (only for logged-in non-creator)
  useEffect(() => {
    const _isCreator = !!(user && script?.creator_id === user.id)
    if (!resolvedOpen || !script || !user || _isCreator) return
    fetch(`/api/marketplace/scripts/${script.id}/review`)
      .then((r) => r.json())
      .then((data) => {
        const mine = (data.reviews || []).find((r: any) => r.reviewer?.id === user.id || r.reviewer_id === user.id)
        if (mine) {
          setUserRating(mine.rating)
          setReviewText(mine.review_text ?? "")
          setExistingReviewId(mine.id)
          setRatingSubmitted(true)
        }
      })
      .catch(() => {})
  }, [resolvedOpen, script?.id, user?.id])

  const handleStartTrial = async () => {
    if (!script) return
    setTrialLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/marketplace/scripts/${script.id}/trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to start trial")

      // Server recognized this user already owns the script — just play it for real
      // instead of opening a restricted trial around content they've already paid for.
      if (data.is_purchased) {
        await handlePlay()
        return
      }

      const funscriptData = data.script_content
      if (!funscriptData?.actions) throw new Error(t("trialContentUnavailable"))
      const durationMs = (data.trial_duration_seconds ?? trialDuration) * 1000

      const userScript: UserScript = {
        id: script.id,
        name: script.title,
        description: script.description ?? "",
        contentUrl: script.linked_content_url ?? undefined,
        contentSite: script.linked_content_site ?? undefined,
        contentType: (script.content_type as any) ?? "video",
        thumbnailUrl: script.thumbnail_url ?? undefined,
        funscript: {
          version: funscriptData.version ?? "1.0",
          inverted: false,
          range: 100,
          actions: funscriptData.actions ?? [],
          metadata: { title: script.title, duration: funscriptData.metadata?.duration ?? durationMs },
        },
        isPublished: true,
        tags: script.tags ?? [],
        deviceCompatibility: [],
        averageIntensity: script.average_intensity ?? 50,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setTrialScript(userScript)
      setTrialCutoffMs(durationMs)
      setIsTrialPaused(false)
      setTrialAttempt((n) => n + 1)
    } catch (err: any) {
      setError(err.message || "Could not start trial")
    } finally {
      setTrialLoading(false)
    }
  }

  const handlePurchase = async (tier: "play" | "download" = "play") => {
    if (!script) return
    if (!user) {
      setError(t("youNeedToBeLoggedInToPurchaseScripts"))
      return
    }
    setError(null)
    try {
      // Preserve the page the user is actually on (path + existing params,
      // e.g. ?post=... for a booru post trialed from its detail modal) —
      // this used to hardcode "/" as the return target, which meant buying a
      // script while previewing a post always dumped the user back on the
      // bare marketplace tab instead of returning to the content they came
      // from. Only purchase/session_id/script_id are added on top.
      const successParams = new URLSearchParams(window.location.search)
      successParams.set("purchase", "success")
      successParams.set("script_id", script.id)
      const res = await fetch("/api/stripe/purchase-script", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptId: script.id,
          tier,
          successUrl: `${window.location.origin}${window.location.pathname}?${successParams.toString()}`,
          cancelUrl: window.location.href,
          ...(couponData ? { couponCode: couponData.code } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to start checkout")
      if (data.url) window.location.href = data.url
    } catch (err: any) {
      setError(err.message || "Purchase failed")
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponInput.trim() || !script) return
    setCouponLoading(true)
    setCouponError(null)
    try {
      const res = await fetch("/api/marketplace/validate-coupon", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: couponInput.trim(),
          items: [{
            scriptId: script.id,
            creatorId: script.creator_id,
            priceCents: script.price_cents ?? 0,
            tier: "play",
          }],
        }),
      })
      const data = await res.json()
      if (!res.ok) { setCouponError(data.error || "Invalid coupon"); return }
      setCouponData({ id: data.coupon.id, code: data.coupon.code, discount_type: data.coupon.discount_type, discount_value: data.coupon.discount_value, discount_cents: data.discount_cents })
      setCouponInput("")
    } catch {
      setCouponError("Failed to validate coupon")
    } finally {
      setCouponLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!script) return
    setIsDownloading(true)
    setError(null)
    try {
      const res = await fetch(`/api/marketplace/scripts/${script.id}/download`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (data.upsell && script.download_price_cents) {
          await handlePurchase("download")
          return
        }
        throw new Error(data.error || "Download failed")
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      const disposition = res.headers.get("Content-Disposition")
      const match = disposition?.match(/filename="([^"]+)"/)
      a.download = match?.[1] || `${script.title}.funscript`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message || "Download failed")
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePlay = async () => {
    if (!script) return
    setIsLoadingPlay(true)
    try {
      const funscriptData = script.script_content
      if (!funscriptData?.actions) throw new Error(t("scriptContentUnavailable"))
      const duration = funscriptData.metadata?.duration
        ?? script.script_duration_ms
        ?? (funscriptData.actions.length > 0 ? funscriptData.actions[funscriptData.actions.length - 1].at : 0)
      const userScript: UserScript = {
        id: script.id,
        name: script.title,
        description: script.description ?? "",
        contentUrl: script.linked_content_url ?? undefined,
        contentSite: script.linked_content_site ?? undefined,
        contentType: (script.content_type as any) ?? "video",
        thumbnailUrl: script.thumbnail_url ?? undefined,
        funscript: {
          version: funscriptData.version ?? "1.0",
          inverted: false,
          range: 100,
          actions: funscriptData.actions ?? [],
          metadata: { title: script.title, duration },
        },
        isPublished: true,
        tags: script.tags ?? [],
        deviceCompatibility: [],
        averageIntensity: script.average_intensity ?? 50,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setScript(userScript)
      onOpenChange(false)
      router.push("/?tab=scripter&editor=script-player")
    } catch {
      setError(t("couldNotLoadScriptContentTryDownloadingI"))
    } finally {
      setIsLoadingPlay(false)
    }
  }

  const handleSubmitRating = async () => {
    if (!script || !userRating) return
    setIsSubmittingRating(true)
    try {
      const res = await fetch(`/api/marketplace/scripts/${script.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating: userRating, review_text: reviewText.trim() || undefined }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setRatingSubmitted(true)
      setExistingReviewId(data.review?.id ?? null)
    } catch (err: any) {
      setError(err.message || "Could not submit rating")
    } finally {
      setIsSubmittingRating(false)
    }
  }

  const reviewPlaceholder = userRating >= 4
    ? "What worked well?"
    : userRating > 0 && userRating <= 2
      ? "What needs work?"
      : "Share what worked and what could be better."

  const handleOpenInEditor = () => {
    if (!script?.script_content?.actions) return
    const funscriptData = script.script_content
    const duration = funscriptData.metadata?.duration
      ?? script.script_duration_ms
      ?? (funscriptData.actions.length > 0 ? funscriptData.actions[funscriptData.actions.length - 1].at : 0)
    const targetEditor = getEditorRoute(script as any)
    const newId = addScript({
      name: `${script.title} (edit)`,
      description: script.description ?? "",
      contentUrl: script.linked_content_url ?? undefined,
      contentSite: script.linked_content_site ?? undefined,
      contentId: script.linked_content_id ?? undefined,
      contentType: (script.content_type as any) ?? "video",
      thumbnailUrl: script.thumbnail_url ?? undefined,
      funscript: {
        version: funscriptData.version ?? "1.0",
        inverted: false,
        range: 100,
        actions: funscriptData.actions,
        metadata: { title: script.title, duration },
      },
      isPublished: false,
      tags: script.tags ?? [],
      deviceCompatibility: [],
      averageIntensity: script.average_intensity ?? 50,
      purchasedFromScriptId: script.id,
      editorMetadata: {
        editorType: targetEditor === "manual-control" ? "manual-editor" : targetEditor as any,
        lastEditorState: funscriptData.metadata?.editorMetadata?.lastEditorState,
      },
    } as any)
    onOpenChange(false)
    router.push(`/?tab=scripter&editor=${targetEditor}&scriptId=${encodeURIComponent(newId)}`)
    toast.info(t("titleLoadedSaveAsPrivateToKeepYour", { title: script.title }))
  }

  if (!script) return null

  // isCreatorVerified comes from the server (more reliable than client-side field comparison)
  const isCreator = isCreatorVerified || !!(user && script.creator_id === user.id)

  return (
    <>
    <Dialog open={resolvedOpen} onOpenChange={onOpenChange}>
      {/* !rounded-lg forced -- same unexplained cascade override as
          patch-notes-modal.tsx hit earlier: className correctly includes
          rounded-lg (confirmed via getComputedStyle), but the painted
          radius measured 2px instead of the theme's 12px until forced. */}
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto !rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            {script.title}
            {!isFree && (
              <Badge className={cn(
                "text-xs font-bold shrink-0",
                isCreator
                  ? "bg-violet-500/20 text-violet-400 border-violet-500/30"
                  : isPurchased
                  ? "bg-green-500/20 text-green-500 border-green-500/30"
                  : "bg-primary/20 text-primary border-primary/30"
              )}>
                {isCreator
                  ? <><CheckCircle2 className="h-3 w-3 mr-1" />{t("creator")}</>
                  : isPurchased
                  ? <><CheckCircle2 className="h-3 w-3 mr-1" />{t("owned")}</>
                  : formatPrice(script.price_cents)}
              </Badge>
            )}
            {isFree && <Badge variant="secondary" className="text-xs shrink-0">{t("free")}</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Thumbnail, or — once "Try" is clicked — the live trial player embedded in this same
              spot (Handy sync, real heatmap, transport controls), rather than a second stacked
              modal. Buy/rating/creator info below stay visible the whole time either way. */}
          <div className="relative rounded-xl overflow-hidden bg-muted aspect-video">
            {trialScript ? (
              <ScriptPlayerCore
                key={trialAttempt}
                script={trialScript}
                open
                onClose={() => setHasTriedScript(true)}
                trialMode
                autoPlay
                showHeader={false}
                previewCutoffMs={trialCutoffMs}
                onCutoffReached={() => setHasTriedScript(true)}
                externalPaused={isTrialPaused}
              />
            ) : (
              <>
                {script.thumbnail_url ? (
                  <img
                    src={script.thumbnail_url}
                    alt={script.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="h-12 w-12 text-muted-foreground/20" />
                  </div>
                )}

                {/* Visual watermark overlay for unpurchased paid scripts */}
                {!isFree && !isPurchased && <PreviewWatermarkOverlay />}

                {/* Heatmap strip at bottom of thumbnail */}
                {script.script_content?.actions?.length > 1 && (
                  <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none" style={{ opacity: 0.9 }}>
                    <ScriptHeatmap
                      actions={script.script_content.actions}
                      className="h-[12px] rounded-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Creator + stats / rating row */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              {/* Left: creator */}
              <div className="flex items-center gap-2 min-w-0">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarImage src={script.creator_avatar || undefined} />
                  <AvatarFallback className="text-xs">
                    {(script.creator_username || "A")[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground truncate">{script.creator_username || "Anonymous"}</span>
                {user && !isCreator && (
                  <FollowButton creatorId={script.creator_id} />
                )}
              </div>

              {/* Right: interactive stars for eligible users, otherwise aggregate stats */}
              {!isCreator && user && (isPurchased || hasTriedScript || isFree) ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        title={["", t("poor"), t("fair"), t("good"), t("great"), t("excellent")][star]}
                        onClick={() => { setUserRating(star); setRatingSubmitted(false) }}
                        onMouseEnter={() => setRatingHover(star)}
                        onMouseLeave={() => setRatingHover(0)}
                        className="p-0.5 transition-transform hover:scale-110 active:scale-95"
                      >
                        <Star className={cn(
                          "h-4 w-4 transition-colors",
                          (ratingHover || userRating) >= star
                            ? "fill-amber-600 text-amber-600 dark:fill-amber-400 dark:text-amber-400"
                            : "text-muted-foreground/30 hover:text-muted-foreground/60"
                        )} />
                      </button>
                    ))}
                  </div>
                  {(ratingHover > 0 || userRating > 0) && (
                    <span className="text-xs text-muted-foreground">
                      {["", t("poor"), t("fair"), t("good"), t("great"), t("excellent")][ratingHover || userRating]}
                    </span>
                  )}
                  {ratingSubmitted && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                </div>
              ) : (
                <div className="flex items-center gap-3 text-sm text-muted-foreground shrink-0">
                  {script.avg_rating > 0 && (
                    <span className="flex items-center gap-1 text-amber-500">
                      <Star className="h-3.5 w-3.5 fill-current" />
                      {script.avg_rating.toFixed(1)}
                      <span className="text-muted-foreground">({script.review_count})</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <ShoppingBag className="h-3.5 w-3.5" />
                    {script.purchase_count.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    {script.view_count.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Review textarea — drops in below the row when a star is clicked */}
            {!isCreator && user && (isPurchased || hasTriedScript || isFree) && userRating > 0 && !ratingSubmitted && (
              <div className="flex items-start gap-2">
                <Textarea
                  placeholder={reviewPlaceholder}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={2}
                  className="text-xs resize-none flex-1"
                />
                <Button size="sm" className="gap-1.5 shrink-0 mt-0.5" onClick={handleSubmitRating} disabled={isSubmittingRating}>
                  {isSubmittingRating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  {existingReviewId ? t("update") : t("submit")}
                </Button>
              </div>
            )}
          </div>

          {/* Description */}
          {script.description && (
            <p className="text-sm text-muted-foreground">{script.description}</p>
          )}

          {/* Tags */}
          {(script.tags?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {script.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}

          {/* Trial info for unpurchased paid scripts (not shown for creator) */}
          {!isFree && !isPurchased && !isCreator && !hasTriedScript && (
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 space-y-2">
              <p className="text-sm font-medium">{t("tryBeforeYouBuy")}</p>
              <p className="text-xs text-muted-foreground">
                {t("previewTheFirstTrialDurationSecondsOfThi", { trialDuration })}
              </p>
            </div>
          )}

          {/* Coupon input — only for logged-in users about to buy an unpurchased paid script */}
          {user && !isFree && !isPurchased && !isCreator && (
            <div className="space-y-1.5">
              {couponData ? (
                <div className="flex items-center gap-2 rounded-lg bg-green-500/10 border border-green-500/20 px-3 py-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium text-green-600">{couponData.code}</span>
                    <span className="text-muted-foreground ml-2">
                      {couponData.discount_type === "percentage" ? `${couponData.discount_value}% off` : `${formatPrice(couponData.discount_value)} off`}
                      {" — saves "}{formatPrice(couponData.discount_cents)}
                    </span>
                  </div>
                  <button onClick={() => { setCouponData(null); setCouponError(null) }} className="text-muted-foreground hover:text-destructive transition-colors">
                    <XCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <SealPercent className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                      <Input
                        placeholder={t("couponCode")}
                        value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(null) }}
                        onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                        className="pl-7 h-8 text-xs uppercase placeholder:normal-case"
                      />
                    </div>
                    <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={handleApplyCoupon} disabled={couponLoading || !couponInput.trim()}>
                      {couponLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {couponError && <p className="text-xs text-destructive flex items-center gap-1"><XCircle className="h-3 w-3" />{couponError}</p>}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {isCheckingOwnership ? (
            <Button disabled className="flex-1">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("checking")}
            </Button>
          ) : (isPurchased || isFree) ? (
            <>
              {/* Download */}
              {script.allow_download && canDownload && (
                <Button variant="outline" size="sm" onClick={handleDownload} disabled={isDownloading}>
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />
                  {isDownloading ? "Downloading…" : ".funscript"}
                </Button>
              )}
              {!isCreator && !canDownload && script.download_price_cents && (
                <Button variant="outline" size="sm" onClick={() => handlePurchase("download")} disabled={patreonMode}>
                  <FileDown className="h-3.5 w-3.5 mr-1.5" />
                  {purchaseTier === "play" ? `Upgrade: Download & Edit — ${formatPrice(script.download_price_cents - script.price_cents)}` : `Download & Edit — ${formatPrice(script.download_price_cents)}`}
                </Button>
              )}
              {/* Creator edit */}
              {isCreator && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  {t("edit")}
                </Button>
              )}
              {/* Purchaser open-in-editor (if creator grants permission) */}
              {!isCreator && isPurchased && script.allow_purchaser_edit && canEdit && (
                <Button variant="outline" size="sm" onClick={handleOpenInEditor}>
                  <PenTool className="h-3.5 w-3.5 mr-1.5" />
                  {t("edit")}
                </Button>
              )}
              <Button className="flex-1" onClick={handlePlay} disabled={isLoadingPlay}>
                {isLoadingPlay ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                {isLoadingPlay ? t("loading") : t("playScript")}
              </Button>
            </>
          ) : (
            <>
              {/* Creator controls for their own paid script */}
              {isCreator && (
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  {t("edit")}
                </Button>
              )}
              {/* Trial button — always stays mounted (disabled while loading) so it never
                  vanishes without a way to retry; clicking it swaps the thumbnail above for a
                  live embedded player (Handy sync, heatmap, transport controls). */}
              {!isCreator && (
                <Button
                  variant="outline"
                  disabled={trialLoading}
                  onClick={() => {
                    if (trialScript && !trialLoading) {
                      setIsTrialPaused((p) => !p)
                    } else {
                      handleStartTrial()
                    }
                  }}
                >
                  {trialLoading ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : trialScript && !isTrialPaused ? (
                    <Pause className="h-3.5 w-3.5 mr-1.5" />
                  ) : (
                    <Hourglass className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {trialLoading
                    ? t("loading")
                    : trialScript && !isTrialPaused
                    ? "Pause"
                    : trialScript && isTrialPaused
                    ? "Resume"
                    : hasTriedScript
                    ? `Try again (${trialDuration}s)`
                    : `Try (${trialDuration}s)`}
                </Button>
              )}
              {isCreator ? (
                <Button className="flex-1" onClick={handlePlay} disabled={isLoadingPlay}>
                  {isLoadingPlay ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  {isLoadingPlay ? t("loading") : t("playScript")}
                </Button>
              ) : patreonMode ? (
                <div className="flex-1">
                  <ProcessorLockedScreen variant="inline" />
                </div>
              ) : script.download_price_cents ? (
                <div className="flex flex-col gap-2 flex-1">
                  <Button className="w-full" onClick={() => handlePurchase("download")}>
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {user ? `Buy + Download & Edit — ${formatPrice(script.download_price_cents)}` : "Log in to Purchase"}
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => handlePurchase("play")}>
                      <Play className="h-4 w-4 mr-2" />
                      {user
                        ? couponData
                          ? `Play only — ${formatPrice(Math.max(50, (script.price_cents ?? 0) - couponData.discount_cents))} (was ${formatPrice(script.price_cents ?? 0)})`
                          : `Play only — ${formatPrice(script.price_cents)}`
                        : "Log in to Purchase"}
                    </Button>
                    {user && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => {
                          if (inCart(script.id)) {
                            removeFromCart(script.id)
                            toast.info(t("removedFromCart"))
                          } else {
                            addToCart({
                              scriptId: script.id,
                              title: script.title,
                              thumbnailUrl: script.thumbnail_url,
                              priceCents: script.download_price_cents!,
                              tier: "download",
                              creatorId: script.creator_id,
                              creatorUsername: script.creator_username || "Unknown",
                            })
                            toast.success(t("addedToCart"), { description: script.title })
                          }
                        }}
                        title={inCart(script.id) ? t("removeFromCart") : t("addDownloadToCart")}
                      >
                        <ShoppingCart className={`h-4 w-4 ${inCart(script.id) ? "fill-current" : ""}`} />
                      </Button>
                    )}
                  </div>
                  <p className="text-center text-[11px] text-muted-foreground">{t("downloadTierIncludesTheFunscriptFileAndI")}</p>
                </div>
              ) : (
                <div className="flex gap-2 flex-1">
                  <Button className="flex-1" onClick={() => handlePurchase("play")}>
                    {user
                      ? couponData
                        ? `Buy Now — ${formatPrice(Math.max(50, (script.price_cents ?? 0) - couponData.discount_cents))} (was ${formatPrice(script.price_cents ?? 0)})`
                        : `Buy Now — ${formatPrice(script.price_cents)}`
                      : "Log in to Purchase"}
                  </Button>
                  {user && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => {
                        if (inCart(script.id)) {
                          removeFromCart(script.id)
                          toast.info(t("removedFromCart"))
                        } else {
                          addToCart({
                            scriptId: script.id,
                            title: script.title,
                            thumbnailUrl: script.thumbnail_url,
                            priceCents: script.price_cents,
                            tier: "play",
                            creatorId: script.creator_id,
                            creatorUsername: script.creator_username || "Unknown",
                          })
                          toast.success(t("addedToCart"), { description: script.title })
                        }
                      }}
                      title={inCart(script.id) ? t("removeFromCart") : t("addToCart")}
                    >
                      <ShoppingCart className={`h-4 w-4 ${inCart(script.id) ? "fill-current" : ""}`} />
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <EditScriptDialog
      script={script}
      open={editOpen}
      onOpenChange={setEditOpen}
      onUpdated={() => { setEditOpen(false); onUpdated?.() }}
    />
    </>
  )
}

// Small inline follow button for script detail modal
function FollowButton({ creatorId }: { creatorId: string }) {
  const t = useTranslations("communityScriptDetailModal")
  const [isFollowing, setIsFollowing] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/users/${creatorId}/follow`)
      .then((r) => r.json())
      .then((d) => setIsFollowing(d.following === true))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [creatorId])

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const method = isFollowing ? "DELETE" : "POST"
    setIsFollowing(!isFollowing)
    await fetch(`/api/users/${creatorId}/follow`, { method }).catch(() => {
      setIsFollowing(isFollowing) // revert on error
    })
  }

  if (loading) return null

  return (
    <Button
      variant={isFollowing ? "outline" : "secondary"}
      size="sm"
      className="h-6 px-2 text-[11px] rounded-full"
      onClick={toggle}
    >
      {isFollowing ? t("following") : t("follow")}
    </Button>
  )
}
